/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC } from 'react';
import React, { useContext, useRef, useCallback } from 'react';
import { useMutation } from '@apollo/react-hooks';
import set from 'lodash/set';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import type { DocNode } from '@atlaskit/adf-schema';
import { ChromelessEditor } from '@atlaskit/editor-core/appearance-editor-chromeless';
import { CommentEditor as AkCommentEditor } from '@atlaskit/editor-core/appearance-editor-comment';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { markErrorAsHandled } from '@confluence/graphql';
import {
	EDIT_PAGE_COMMENT_LOAD_EXPERIENCE,
	EDIT_PAGE_COMMENT_PUBLISH_EXPERIENCE,
	EDIT_INLINE_COMMENT_LOAD_EXPERIENCE,
	EDIT_INLINE_COMMENT_PUBLISH_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { CommentEditor } from '@confluence/comment';
import type { CommentType } from '@confluence/comments-data';
import { useCommentsData, CommentType as CommentTypeEnum } from '@confluence/comments-data';
import { ReactionsContext, useCommentsContentActions } from '@confluence/comment-context';
import { useSessionData } from '@confluence/session-data';
import type {
	GraphQLContentStatus,
	CommentsPanelQueryType,
	InlineCommentQueryType,
	ActiveInlineCommentsQueryType,
} from '@confluence/inline-comments-queries';
import {
	InlineCommentQuery,
	CommentsPanelQuery,
	ActiveInlineCommentsQuery,
} from '@confluence/inline-comments-queries';
import { getLogger } from '@confluence/logger';
import { END } from '@confluence/navdex';
import { updateFooterApolloCacheOnEdit } from '@confluence/page-comments-queries/entry-points/pageCommentUtils';
import { fg } from '@confluence/feature-gating';
import { withFlags } from '@confluence/flags';
import type { WithFlagsProps } from '@confluence/flags';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';

import { ContentRepresentation } from './__types__/EditCommentUpdateInlineCommentMutation';
import type {
	EditCommentUpdateInlineCommentMutation as EditCommentUpdateInlineCommentMutation$data,
	EditCommentUpdateInlineCommentMutationVariables as EditCommentUpdateInlineCommentMutation$variables,
} from './__types__/EditCommentUpdateInlineCommentMutation';
import { WithCurvedBranchingStyle } from './WithCurvedBranchingStyle';
import type { InlineCommentsMode } from './inlineCommentsTypes';
import {
	parseError,
	getTranslatedError,
	isUnexpectedError,
	isAlreadyDeletedError,
} from './inlineCommentsErrorUtils';
import { CommentAuthor } from './CommentAuthor';

const EditCommentUpdateInlineCommentMutation = gql`
	mutation EditCommentUpdateInlineCommentMutation($input: UpdateCommentInput!) {
		updateComment(input: $input) {
			id
		}
	}
`;
const i18n = defineMessages({
	cannotEditTitle: {
		id: 'inline-comments-common.could.not.edit.title',
		defaultMessage: 'Unable to edit comment',
		description:
			'The title of the flag shown to a user that says their comment could not be edited',
	},
});

const logger = getLogger('inline-comments-common:edit-comment');

type EditCommentProps = {
	pageId: string;
	pageType: string;
	annotationId?: string;
	commentId: string;
	spaceId: string;
	content: string;
	displayCommentInViewMode: () => void;
	isReply: boolean;
	avatarUrl: string;
	displayName: string;
	mode: InlineCommentsMode;
	hasMediaUploadPermissions: boolean;
	isCommentsPanel?: boolean;
	isFooterComment?: boolean;
	adjustForFooterReplies?: boolean;
	isLastNestedFooterReply?: boolean;
	onCancelEdit?: () => void;
	/**
	 * Return a list of inline node types, which are wrapped by the annotation,
	 * for annotation with given ID.
	 *
	 * The `undefined` will be returned if `editor_inline_comments_on_inline_nodes` is off.
	 *
	 * @todo: Do not forget to remove `undefined` when the
	 *        `editor_inline_comments_on_inline_nodes` is removed.
	 */
	getInlineNodeTypes?: (annotationId: string) => string[] | undefined;
	commentType: CommentType;
	isStandaloneComment?: boolean;
};

const editorContainerStyle = xcss({
	paddingBlock: 'space.150',
	paddingInline: 'space.200',
});

const editorContainerForFooterWrapperStyle = xcss({
	padding: 'unset',
	paddingTop: 'space.100',
});

const editorContainerForFooterExtraLeftPaddingWithBorderStyle = xcss({
	paddingLeft: 'space.300',
	marginLeft: 'space.200',
	marginTop: 'space.050', // If we're doing the border here, we need margin instead of padding so the border is flush with the editor
	borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
});

const editorBottomPaddingStyle = xcss({
	paddingBottom: 'space.100',
});

const EditCommentComponent: FC<EditCommentProps & WithFlagsProps> = ({
	pageId,
	pageType,
	annotationId,
	commentId,
	spaceId,
	content,
	displayCommentInViewMode,
	isReply,
	avatarUrl,
	displayName,
	mode,
	hasMediaUploadPermissions,
	isCommentsPanel,
	isFooterComment = false,
	adjustForFooterReplies = false,
	isLastNestedFooterReply = false,
	onCancelEdit,
	getInlineNodeTypes,
	commentType,
	flags,
	isStandaloneComment,
}) => {
	const { userId, displayName: sessionDisplayName } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { onChange, resetContentChanged } = useCommentsContentActions();
	const { isReactionsEnabled } = useContext(ReactionsContext);
	const [, { updateCommentBody }] = useCommentsData();
	const commentContainerElementRef = useRef<HTMLDivElement | null>(null);
	const pageMode = useGetPageMode();
	const [updateCommentFn] = useMutation<
		EditCommentUpdateInlineCommentMutation$data,
		EditCommentUpdateInlineCommentMutation$variables
	>(EditCommentUpdateInlineCommentMutation);
	const handleOnEditorReady = () => {
		if (fg('confluence_frontend_separate_add_page_comment')) {
			if (commentType === CommentTypeEnum.GENERAL) {
				experienceTracker.succeed({
					name: EDIT_PAGE_COMMENT_LOAD_EXPERIENCE,
				});
			} else {
				experienceTracker.succeed({
					name: EDIT_INLINE_COMMENT_LOAD_EXPERIENCE,
				});
			}
		} else {
			experienceTracker.succeed({
				name: EDIT_INLINE_COMMENT_LOAD_EXPERIENCE,
			});
		}
	};

	const handleOnCancel = () => {
		displayCommentInViewMode();
		resetContentChanged();
		onCancelEdit && onCancelEdit();
	};

	const handleUpdateComment = (adf: JSONDocNode, onSuccess: () => void) => {
		if (fg('confluence_frontend_separate_add_page_comment')) {
			if (commentType === CommentTypeEnum.GENERAL) {
				experienceTracker.start({
					name: EDIT_PAGE_COMMENT_PUBLISH_EXPERIENCE,
					attributes: {
						mode: pageMode,
					},
				});
			} else {
				experienceTracker.start({
					name: EDIT_INLINE_COMMENT_PUBLISH_EXPERIENCE,
					attributes: {
						mode: pageMode,
					},
				});
			}
		} else {
			experienceTracker.start({
				name: EDIT_INLINE_COMMENT_PUBLISH_EXPERIENCE,
				attributes: {
					mode: pageMode,
				},
			});
		}

		const adfString = JSON.stringify(adf);
		const variables = {
			input: {
				commentId,
				commentBody: {
					value: adfString,
					representationFormat: ContentRepresentation.ATLAS_DOC_FORMAT,
				},
			},
		};

		const getQuery = () => {
			if (isCommentsPanel || isFooterComment) {
				return CommentsPanelQuery;
			} else if (isStandaloneComment && fg('confluence-frontend-redesigned-standalone-comment')) {
				return ActiveInlineCommentsQuery;
			} else {
				return InlineCommentQuery;
			}
		};

		const getQueryVariable = () => {
			if (isCommentsPanel || isFooterComment) {
				return {
					pageId,
				};
			} else if (isStandaloneComment && fg('confluence-frontend-redesigned-standalone-comment')) {
				return {
					inlineMarkerRefList: [annotationId],
					pageId,
					contentStatus: ['DRAFT', 'CURRENT'] as GraphQLContentStatus[],
				};
			} else {
				return {
					annotationId,
					pageId,
					contentStatus: ['DRAFT', 'CURRENT'] as GraphQLContentStatus[],
				};
			}
		};

		return updateCommentFn({
			variables,
			update: (cache) => {
				// readQuery can still return null which complicates TS lint errors with optional chaining
				// once we move to v3 of the apollo client we can move to cache.modify
				try {
					const dataProxy = cache.readQuery({
						query: getQuery(),
						variables: getQueryVariable(),
					});

					type QueryType =
						| CommentsPanelQueryType
						| InlineCommentQueryType
						| ActiveInlineCommentsQueryType;

					const data: QueryType = Object.assign({} as any, dataProxy);

					let oldComment;
					// Standalone only has 1 comment so use index 0
					const idxToUpdate =
						isCommentsPanel || isFooterComment
							? data?.comments?.nodes?.findIndex((n) => n?.id === commentId) || -1
							: 0;

					if (idxToUpdate === -1) {
						return;
					}

					// find the comment to update for a reply or the parent comment
					if (isReply) {
						// @ts-ignore
						const replies = data?.comments?.nodes[idxToUpdate].replies ?? [];

						replies.forEach((reply) => {
							// @ts-ignore
							if (reply?.id === commentId) {
								oldComment = reply;
							}
						});
					} else {
						// @ts-ignore
						oldComment = data?.comments?.nodes[idxToUpdate];
					}

					if (oldComment) {
						set(oldComment, 'body.value', adfString);
						set(oldComment, 'version.number', (oldComment.version?.number ?? 0) + 1);
						set(oldComment, 'version.when', new Date().toISOString());
						set(oldComment, 'version.by.displayName', sessionDisplayName);
					}

					cache.writeQuery({
						query: getQuery(),
						variables: getQueryVariable(),
						data,
					});
				} catch (error) {
					logger.error`An Error occurred when updating cache for edit reply in panel or inline - ${error}`;
				}

				if (commentType === CommentTypeEnum.GENERAL) {
					updateFooterApolloCacheOnEdit(
						commentId,
						adfString,
						pageId,
						isReactionsEnabled,
						cache,
						sessionDisplayName,
					);
				}
			},
		})
			.then(() => {
				if (fg('confluence_frontend_separate_add_page_comment')) {
					if (commentType === CommentTypeEnum.GENERAL) {
						experienceTracker.succeed({
							name: EDIT_PAGE_COMMENT_PUBLISH_EXPERIENCE,
							attributes: {
								mode: pageMode,
								type: isReply ? 'reply' : 'topLevel',
							},
						});
					} else {
						experienceTracker.succeed({
							name: EDIT_INLINE_COMMENT_PUBLISH_EXPERIENCE,
							attributes: {
								mode: pageMode,
								type: isReply ? 'reply' : 'topLevel',
							},
						});
					}
				} else {
					experienceTracker.succeed({
						name: EDIT_INLINE_COMMENT_PUBLISH_EXPERIENCE,
						attributes: {
							mode: pageMode,
							type: isReply ? 'reply' : 'topLevel',
						},
					});
				}

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'updated',
						actionSubject: 'comment',
						actionSubjectId: commentId,
						objectType: pageType,
						objectId: pageId,
						source: pageMode === PageMode.VIEW ? 'viewPageScreen' : 'editPageScreen',
						attributes: {
							commentType,
							pageType,
							navdexPointType: END,
							isLivePage: pageMode === PageMode.LIVE,
							inlineNodeTypes:
								getInlineNodeTypes && annotationId ? getInlineNodeTypes(annotationId) : undefined,
						},
					},
				}).fire();

				annotationId &&
					updateCommentBody(commentId, annotationId, adfString, commentType, sessionDisplayName);

				// reset the editor
				onSuccess();

				resetContentChanged();

				displayCommentInViewMode();
			})
			.catch((error) => {
				const { message } = parseError(error);
				const translatedError = getTranslatedError(message, commentId);

				if (fg('confluence_frontend_show_already_deleted_flag')) {
					if (isAlreadyDeletedError(translatedError)) {
						void flags.showWarningFlag({
							title: <FormattedMessage {...i18n.cannotEditTitle} />,
							description: <FormattedMessage {...translatedError} />,
						});

						experienceTracker.abort({
							name: EDIT_INLINE_COMMENT_PUBLISH_EXPERIENCE,
							reason: 'Comment already deleted',
						});
					}
				}

				if (isUnexpectedError(translatedError)) {
					experienceTracker.stopOnError({
						name: EDIT_INLINE_COMMENT_PUBLISH_EXPERIENCE,
						error,
					});
				} else {
					// These errors will not be reported to splunk/signalfx through experience tracking as they are expected
					markErrorAsHandled(error);
				}

				return Promise.reject({
					error: translatedError,
				});
			});
	};

	let parsedContent: DocNode | undefined = undefined;
	try {
		parsedContent = JSON.parse(content);
	} catch (e) {
		logger.error`Unable to parse content ADF, ${e}`;
	}

	const showBranchingStyle = isFooterComment && isReply;

	// Create a wrapper for the editor component. If we have a branching style, the wrapper needs to use withBranchingStyle, otherwise just return as normal
	const BranchingStyleWrapper = useCallback(
		({ children }: { children: React.ReactNode }) => {
			return showBranchingStyle ? (
				<WithCurvedBranchingStyle
					showDefaultStyles
					customStyles={{
						// If we don't have children, line up with the author
						// If we do have children, line up with the bottom of the editor toolbar
						height: adjustForFooterReplies ? '25px' : '90px',
						left: isLastNestedFooterReply ? '-24px' : '-25px',
					}}
				>
					{children}
				</WithCurvedBranchingStyle>
			) : (
				<>{children}</>
			);
		},
		[showBranchingStyle, adjustForFooterReplies, isLastNestedFooterReply],
	);

	return (
		<BranchingStyleWrapper>
			<Box
				xcss={[editorContainerStyle, isFooterComment && editorContainerForFooterWrapperStyle]}
				data-cy="editor-edit-container"
				ref={commentContainerElementRef}
			>
				<CommentAuthor
					commentMode="edit"
					userId={userId}
					displayName={displayName}
					avatarUrl={avatarUrl}
				/>
				<Box
					xcss={[
						isFooterComment && editorBottomPaddingStyle,
						adjustForFooterReplies && editorContainerForFooterExtraLeftPaddingWithBorderStyle,
					]}
				>
					<CommentEditor
						pageId={pageId}
						pageType={pageType}
						spaceId={spaceId}
						appearance={isFooterComment ? 'comment' : 'chromeless'}
						EditorComponent={isFooterComment ? AkCommentEditor : ChromelessEditor}
						commentMode="edit"
						commentType={mode === 'view' ? 'inline' : 'edit-inline'}
						onSaveComment={handleUpdateComment}
						content={parsedContent}
						onCancelComment={handleOnCancel}
						onContentChange={onChange}
						showCancelButton
						useNewWarningModal
						onEditorReady={handleOnEditorReady}
						hideWatchCheckbox
						isFooterComment={isFooterComment}
						pageMode={mode}
						hasMediaUploadPermissions={hasMediaUploadPermissions}
						commentContainerElementRef={commentContainerElementRef}
						isStandaloneComment={isStandaloneComment}
					/>
				</Box>
			</Box>
		</BranchingStyleWrapper>
	);
};

export const EditComment = withFlags(EditCommentComponent);
